import type {
  Erc721NftId,
  Erc1155NftId,
  EvmAccountId,
  EvmTransactionSource,
  Hex,
  LowerHex,
} from '../types'
import { isEvmAccountId, isHex, toLowerHex } from '../types'

type CreateEvmAddressIdParameters = {
  readonly chainId: number
  readonly address: LowerHex
}

export const createEvmAddressId = ({
  chainId,
  address,
}: CreateEvmAddressIdParameters): EvmAccountId =>
  `evm_address_${chainId}_${address}`

type CreateEvmActionSourceParameters = {
  readonly chainId: number
  readonly transactionHash: Hex
}

export const createEvmActionSource = ({
  chainId,
  transactionHash,
}: CreateEvmActionSourceParameters): EvmTransactionSource =>
  `evm_${chainId}_${toLowerHex(transactionHash)}`

type GetEvmAddressDataOfEvmAddressIdReturnType = {
  readonly chainId: number
  readonly address: LowerHex
}

export const createEvmCurrencyId = ({
  chainId,
  address,
}: {
  readonly chainId: number
  readonly address: LowerHex
}): `evm_${number}_0x${Lowercase<string>}` => `evm_${chainId}_${address}`

export const createErc721NftId = ({
  chainId,
  address,
  tokenId,
}: {
  readonly chainId: number
  readonly address: LowerHex
  tokenId: bigint
}): Erc721NftId => `evm_721_${chainId}_${address}_${tokenId}`

export const createErc1155NftId = ({
  chainId,
  address,
  tokenId,
}: {
  readonly chainId: number
  readonly address: LowerHex
  tokenId: bigint
}): Erc1155NftId => `evm_1155_${chainId}_${address}_${tokenId}`

export const getEvmAddressDataOfEvmAddressId = (
  id: string,
): GetEvmAddressDataOfEvmAddressIdReturnType => {
  if (!isEvmAccountId(id)) {
    throw new Error(`Invalid EVM address ID: ${id}`)
  }
  const [chainId, address] = id.split('_').slice(2)
  if (!isHex(address)) {
    throw new Error(`Invalid EVM address ID: ${id}`)
  }
  return { chainId: Number(chainId), address: toLowerHex(address) }
}

type IncludesEvmAddressIdParameters = {
  readonly addresses: readonly LowerHex[]
  readonly id: string | undefined
}

export const includesEvmAddressId = ({
  addresses,
  id,
}: IncludesEvmAddressIdParameters) => {
  if (id === undefined) {
    return false
  }
  const { address } = getEvmAddressDataOfEvmAddressId(id)
  return addresses.includes(address)
}
